import { ref, computed, watch } from 'vue';

const BADGE_COLORS = [
  { name: 'Blue (Default)', value: 'bg-blue-500', hex: '#3b82f6' },
  { name: 'Red', value: 'bg-red-500', hex: '#ef4444' },
  { name: 'Orange', value: 'bg-orange-500', hex: '#0066ff' },
  { name: 'Yellow', value: 'bg-yellow-500', hex: '#eab308' },
  { name: 'Green', value: 'bg-green-500', hex: '#22c55e' },
  { name: 'Purple', value: 'bg-purple-500', hex: '#a855f7' },
  { name: 'Pink', value: 'bg-pink-500', hex: '#ec4899' },
];

const STORAGE_KEY = 'wlab_unread_badge_settings';

// Default settings
const defaultSettings = {
  color: 'bg-red-500',
  animationEnabled: false,
};

// Get settings from localStorage
const getStoredSettings = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...defaultSettings, ...parsed };
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[Badge Settings] Error reading badge settings:', error);
  }
  return defaultSettings;
};

// Save settings to localStorage
const saveSettings = settings => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[Badge Settings] Error saving badge settings:', error);
  }
};

// Global reactive state - shared across all component instances
const badgeSettings = ref(getStoredSettings());

export function useUnreadBadgeSettings() {
  // Computed properties that derive from the global state
  const badgeColor = computed(() => {
    return badgeSettings.value.color;
  });

  const animationEnabled = computed(() => {
    return badgeSettings.value.animationEnabled;
  });

  const badgeHexColor = computed(() => {
    const colorObj = BADGE_COLORS.find(
      c => c.value === badgeSettings.value.color
    );
    return colorObj ? colorObj.hex : '#ef4444';
  });

  const updateBadgeColor = color => {
    badgeSettings.value = {
      ...badgeSettings.value,
      color,
    };
    saveSettings(badgeSettings.value);
  };

  const updateAnimationEnabled = enabled => {
    badgeSettings.value = {
      ...badgeSettings.value,
      animationEnabled: enabled,
    };
    saveSettings(badgeSettings.value);
  };

  const resetToDefaults = () => {
    badgeSettings.value = { ...defaultSettings };
    saveSettings(badgeSettings.value);
  };

  // Get animation classes based on count
  const getAnimationClasses = count => {
    if (!animationEnabled.value) return '';

    // More urgent animations for higher counts
    if (count >= 10) {
      return 'animate-bounce';
    }
    if (count >= 5) {
      return 'animate-pulse';
    }
    return 'transition-all duration-300 ease-in-out hover:scale-110';
  };

  // Get display text for badge (smart display)
  const getDisplayText = count => {
    if (count === 0) return '';
    if (count <= 9) return count.toString();
    if (count <= 99) return count.toString();
    return '99+';
  };

  return {
    badgeColor,
    badgeHexColor,
    animationEnabled,
    updateBadgeColor,
    updateAnimationEnabled,
    resetToDefaults,
    getAnimationClasses,
    getDisplayText,
    availableColors: BADGE_COLORS,
  };
}
